/**
 * Axios instance for Seedz Manage API.
 *
 * Three interceptors (registration order):
 *  1. Request  — injects Authorization + request-id headers.
 *  2. Response (401) — single-flight silent token refresh; replays queue.
 *  3. Response (error) — normalises every rejection to a plain Error with
 *     a `code` property matching the server's envelope `error.code`.
 *
 * Contract: baseURL '/api/v1', withCredentials: true (httpOnly refresh cookie).
 * The access token lives in Zustand memory only — never in localStorage.
 */
import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { randomUUID } from '@/lib/uuid';

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Single-flight refresh state
// ---------------------------------------------------------------------------
let refreshPromise: Promise<string> | null = null;

// Auth paths that must never trigger the 401 → refresh loop
const AUTH_PATHS = ['/auth/login', '/auth/refresh'];

function isAuthPath(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_PATHS.some((p) => url.includes(p));
}

// ---------------------------------------------------------------------------
// Interceptor 1: Request — attach Authorization + request-id
// ---------------------------------------------------------------------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  config.headers.set('request-id', randomUUID());
  return config;
});

// ---------------------------------------------------------------------------
// Interceptor 2: Response (401) — single-flight refresh then replay
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retried?: boolean;
    };

    if (
      error.response?.status === 401 &&
      !original._retried &&
      !isAuthPath(original.url)
    ) {
      original._retried = true;

      try {
        // Single-flight: all concurrent 401s share one refresh call.
        if (!refreshPromise) {
          refreshPromise = api
            .post<{ data: { accessToken: string } }>('/auth/refresh', {})
            .then((res) => res.data?.data?.accessToken ?? '')
            .finally(() => {
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        if (!newToken) throw new Error('Empty token from refresh');

        // Persist the new token in the auth store.
        const store = useAuthStore.getState();
        store.setLogin(newToken, store.role, store.email, store.mustChangePassword, store.memberId);

        // Replay the original request with the new token.
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return api(original);
      } catch (refreshError) {
        // Refresh failed — clear session, let the caller/router redirect.
        useAuthStore.getState().clearAuth();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Interceptor 3: Response (error) — normalise to Error with .code
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const envelope = error.response?.data as
        | { error?: { code?: string; message?: string } }
        | undefined;
      const code = envelope?.error?.code ?? 'UNKNOWN';
      const message =
        envelope?.error?.message ?? error.message ?? 'An unexpected error occurred.';
      const normalized = Object.assign(new Error(message), { code });
      return Promise.reject(normalized);
    }
    return Promise.reject(error);
  },
);
